import { MaintenanceModeProvider } from '@gc-bridge/hooks/useMaintenanceMode';
import { AuthenticationFailure, ErrorButtonPage, Home } from '@gc-bridge/pages';
import { getEmbeddedApps } from '@gcportal/base';
import { LoginCallback } from '@gc-bridge/components/OktaReact/LoginCallback';
import { Layout } from 'apps/shell/src/layouts/BaseLayout';
import { partition } from 'lodash';
import { useCallback, useMemo } from 'react';
import { createBrowserRouter, Navigate } from 'react-router';
import { RouterProvider } from 'react-router/dom';
import { RemoteRouteProvider } from './RemoteRouteProvider';

interface RouteProps {
  path: string;
  withLayout: boolean;
  additionalRoutes?: { path: string; redirectPath: string }[];
  remoteName: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  config?: any;
}

export function Router() {
  // Loading all embedded apps initially to construct the routes
  const routes = useMemo<RouteProps[]>(() => {
    const apps = getEmbeddedApps();

    return apps.map(({ route, withLayout, additionalRoutes, remoteName }) => ({
      path: route + '/*',
      withLayout,
      additionalRoutes: additionalRoutes?.map((additionalRoute) => ({
        path: additionalRoute.path,
        redirectPath: `/${route}${additionalRoute.redirectPath}`.replace(/\/+/g, '/'),
      })),
      remoteName,
    }));
  }, []);

  const [withLayout, withoutLayout] = useMemo(
    () => partition(routes, (route) => route.withLayout),
    [routes]
  );

  const additionalRoutes = useMemo(
    () =>
      (routes ?? [])
        .flatMap((route) => route.additionalRoutes)
        .filter((additionalRoute) => !!additionalRoute)
        .map((additionalRoute) => ({
          path: additionalRoute.path,
          element: <Navigate to={additionalRoute.redirectPath} replace />,
        })),
    [routes]
  );

  const constructRoute = useCallback(
    ({ path, remoteName }: RouteProps) => ({
      path,
      errorElement: (
        <ErrorButtonPage
          title="An error occurred while trying to load the app."
          content="Please try again later."
          buttonLabel="Back to Bridge"
          buttonUrl="/"
        />
      ),
      element: <RemoteRouteProvider remoteName={remoteName} />,
    }),
    []
  );

  const router = useMemo(
    () =>
      createBrowserRouter([
        {
          path: '/login/callback',
          element: (
            <LoginCallback
              errorComponent={({ error }) => <AuthenticationFailure error={error} />}
            />
          ),
        },
        {
          path: '/',
          element: <Layout routesWithLayout={withLayout} />,
          children: [
            { index: true, element: <Home /> },
            ...withLayout.map(constructRoute),
          ],
        },
        ...withoutLayout.map(constructRoute),
        ...additionalRoutes,
      ]),
    [withLayout, constructRoute, withoutLayout, additionalRoutes]
  );

  return (
    <MaintenanceModeProvider>
      <RouterProvider router={router} />
    </MaintenanceModeProvider>
  );
}
